import * as React from "react"
import { Autocomplete, Box, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import Grid from "@mui/material/Grid2"
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import { AXIOS_API_INSTANCE } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import dayjs from "dayjs"
import onFilterChange from "../../Utils/OnFilterChange"
import { useSnackbar } from 'notistack';
import { DataGrid, } from "@mui/x-data-grid"
import { SimpleCustomToolbar } from "../CustomToolbar"
import { useNavigate } from "react-router-dom";
import DocumentTitle from "../../Utils/DocumentTitle";
import { formatDuration } from "../../Utils"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { useTrackedLocation, useTrackedUser } from "../UserState"
import { build_users_list, findUser, useUsers } from "../ReactQuery/QueryUsers"
import UserAvatar from "../UserAvatar"
import ServiceWorkHistoryModal from "./Modals/ServiceWorkHistoryModal"
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";

export default function ServiceTechnicianWorkHistory() {
    DocumentTitle('Service Technician Work History')
    const { enqueueSnackbar } = useSnackbar();
    const [selectedLocation,] = useTrackedLocation();
    const [my_user,] = useTrackedUser();
    const { data: users, isLoadingUsers } = useUsers();
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`serviceorderworkhistories:pageSize`) || 10),
        page: 0,
    });
    const [filteredTechnician, setFilteredTechnician] = React.useState(undefined)

    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'end_time', sort: 'desc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(true)
    const [startDatetime, setStartDateTime] = React.useState(dayjs().utc().subtract(30, 'day').startOf('day'))
    const [endDatetime, setEndDateTime] = React.useState(dayjs().utc().endOf('day'))
    const [selectedPeriod, setSelectedPeriod] = React.useState(30)
    const [workHistoryModal, setWorkHistoryModal] = React.useState({
        open: false,
        serviceOrderWorkHistory: {},
        creatingNewWorkHistory: false,
    })
    const navigate = useNavigate();

    const createWorkHistory = () => {
        setWorkHistoryModal({
            serviceOrderWorkHistory: {},
            open: true,
            creatingNewWorkHistory: true,
        })
    }
    const editWorkHistoryRowClick = (row) => {
        setWorkHistoryModal({
            serviceOrderWorkHistory: row,
            open: true,
            creatingNewWorkHistory: false,
        })
    }
    const closeModal = (results) => {
        if (results) {
            getServiceOrderWorkHistories(paginationModel, sorting.field, sorting.sort, filtering)
        }
        setWorkHistoryModal({
            open: false,
            serviceOrderWorkHistory: {},
            creatingNewWorkHistory: false,
        })
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`serviceorderworkhistories:pageSize`, model.pageSize)
        setPaginationModel(model)
    }

    const getServiceOrderWorkHistories = (paginationModel, sortField, sortDirection, filterVar) => {
        setIsLoading(true)
        let my_filters = {}
        if (endDatetime > dayjs.utc()) {
            my_filters['and'] = [
                {
                    'or': [
                        {
                            'and': [
                                { 'end_time.gte': startDatetime },
                                { 'end_time.lte': endDatetime }
                            ]
                        },
                        { 'end_time.eq': null }
                    ]
                }
            ]
        } else {
            my_filters['and'] = [
                { 'end_time.gte': startDatetime },
                { 'end_time.lte': endDatetime },
            ]
        }
        if (filteredTechnician) {
            my_filters['and'].push({ 'user_id.eq': filteredTechnician.id })
        }
        if (selectedLocation.id !== "all") {
            my_filters['and'].push({ 'location_id.eq': selectedLocation.id })
        }
        if (Object.keys(filterVar).length > 0) {
            my_filters["and"].push(filterVar)
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("serviceorderworkhistory", {
                params: {
                    page_num: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    order_by: sortField,
                    order_by_direction: sortDirection,
                    filters: JSON.stringify(my_filters)
                },
            })
                .then((serviceorderworkhistories) => {
                    setRowData({
                        rows: serviceorderworkhistories.data.data,
                        total_count: serviceorderworkhistories.data.total_count,
                    })
                    setIsLoading(false)
                }
                ).catch((error) => {
                    enqueueSnackbar(`Failed to load service order warranty claims: ${error.response?.data?.message ?? "Unknown Error Occurred"}`, { variant: 'error' })
                    setIsLoading(false)
                })
        )
    }

    React.useEffect(() => {
        getServiceOrderWorkHistories(paginationModel, sorting.field, sorting.sort, filtering)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, filtering, sorting, startDatetime, endDatetime, selectedLocation, filteredTechnician])

    return (
        (<Box sx={{ flexGrow: 1, flexShrink: 0, flexBasis: 0, overflow: 'hidden' }}>
            <Grid container spacing={1} alignItems={'flex-start'}>
                <Grid container columnSpacing={1} sx={{ pt: 1, mb: 0.5 }} direction="row" justifyContent="space-between" alignItems="center" size={12}>
                    <Grid size="auto" container columnSpacing={1}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Grid size="auto">
                                <DatePicker
                                    value={startDatetime}
                                    label="Start Date"
                                    onChange={(newValue) => {
                                        setStartDateTime(newValue.startOf('day'))
                                    }}
                                    onAccept={(newValue) => {
                                        setSelectedPeriod('custom')
                                        if (newValue > endDatetime) {
                                            setEndDateTime(newValue.endOf('day').add(1, 'days'))
                                        }
                                    }}
                                    sx={{ width: '175px' }}
                                />
                            </Grid>
                            <Grid size="auto">
                                <DatePicker
                                    value={endDatetime}
                                    label="End Date"
                                    onChange={(newValue) => {
                                        setSelectedPeriod('custom')
                                        setEndDateTime(newValue.endOf('day'))
                                    }}
                                    sx={{ width: '175px' }}
                                />
                            </Grid>
                            <Grid size="auto">
                                <FormControl>
                                    <InputLabel id="reports-time-category">Period</InputLabel>
                                    <Select
                                        labelId="reports-time-category"
                                        label="Period"
                                        placeholder=''
                                        onChange={(event) => {
                                            setSelectedPeriod(event.target.value)
                                            if (event.target.value !== 'custom') {
                                                setStartDateTime(dayjs().utc().subtract(event.target.value, 'day').startOf('day'))
                                                setEndDateTime(dayjs().utc().endOf('day'))
                                            }
                                        }}
                                        value={selectedPeriod}
                                        sx={{ width: '115px' }}
                                    >
                                        <MenuItem key="30" value={30}
                                        >
                                            30 days
                                        </MenuItem>
                                        <MenuItem key="60" value={60}
                                        >
                                            60 days
                                        </MenuItem>
                                        <MenuItem key="90" value={90}
                                        >
                                            90 days
                                        </MenuItem>
                                        <MenuItem key="custom" value={'custom'}
                                        >
                                            Custom
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </LocalizationProvider>
                    </Grid>
                    <Grid size="auto">
                        <Box display="flex" justifyContent="flex-end">
                            <Autocomplete
                                sx={{ color: 'rgba(0, 0, 0, 0.6)', maxWidth: '350px', minWidth: '300px' }}
                                getOptionLabel={(option) => `${option.name || ""}`}
                                options={build_users_list(users, my_user, false, false, true, selectedLocation?.id ?? 'all')}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                autoComplete
                                fullWidth
                                includeInputInList
                                // disableClearable
                                value={filteredTechnician}
                                noOptionsText="No Users"
                                loading={isLoadingUsers}
                                disabled={isLoading}
                                onChange={(event, newValue) => {
                                    setFilteredTechnician(newValue)
                                }}
                                slotProps={{
                                    popper: {
                                        sx: {
                                            minWidth: '250px'
                                        }
                                    }
                                }}
                                renderInput={(params) => {
                                    let avatar = undefined
                                    if (params.inputProps["aria-expanded"]) {
                                        avatar = (
                                            <span><UserAvatar user={{}} /></span>
                                        )
                                    } else {
                                        avatar = (
                                            <span><UserAvatar user={(findUser(users, filteredTechnician?.id) ?? filteredTechnician) ?? {}} /></span>
                                        )
                                    }
                                    return (
                                        (<div style={{ position: "relative" }}>
                                            <span
                                                style={{
                                                    position: "absolute",
                                                    transform: "translateY(50%)",
                                                    marginLeft: "5px"
                                                }}
                                            >
                                                {avatar}
                                            </span>
                                            <TextField
                                                {...params}
                                                label="Filter by Technician"
                                                fullWidth
                                                autoComplete='off'
                                                type="text"
                                                slotProps={{
                                                    htmlInput: {
                                                        ...params.inputProps,
                                                        style: {
                                                            padding: "4px 4px 0 32px",
                                                            height: '36px'
                                                        }
                                                    },

                                                    inputLabel: {
                                                        shrink: true,
                                                        ...params.InputLabelProps,
                                                    }
                                                }} />
                                        </div>)
                                    );
                                }}
                                renderOption={(props, option, { inputValue }) => {
                                    const matches = match(props.key, inputValue, { insideWords: true, findAllOccurrences: true });
                                    const parts = parse(props.key, matches);
                                    const li_props = { ...props }
                                    delete li_props.key

                                    return (
                                        (<li key={props.id} {...li_props}>
                                            <Grid container space={1} alignItems="center">
                                                <Grid sx={{ pr: 1 }} size="auto">
                                                    <span><UserAvatar user={option} /></span>
                                                </Grid>
                                                <Grid size="auto">
                                                    {parts.map((part, index) => (
                                                        <span
                                                            key={index}
                                                            style={{
                                                                fontWeight: part.highlight ? 700 : 400,
                                                            }}
                                                        >
                                                            {part.text}
                                                        </span>
                                                    ))}
                                                </Grid>
                                            </Grid>
                                        </li>)
                                    );
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid size="auto">
                        <Button onClick={createWorkHistory} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Add Record</Box></Button>
                    </Grid>
                </Grid>
                <Grid size={12}>
                    <DataGrid
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={rowData.rows}
                        rowCount={rowData.total_count}
                        columns={[
                            {
                                field: "start_time",
                                headerName: "Date",
                                width: 235,
                                editable: false,
                                sortable: true,
                                filterable: false,
                                renderCell: (params) => {
                                    return <span>{dayjs.utc(params.value).local().format('M/D/YYYY')} ({dayjs.utc(params.value).local().format('h:mmA')} - {params.row.end_time ? dayjs.utc(params.row.end_time).local().format('h:mmA') : 'N/A'})</span>
                                }
                            },
                            {
                                field: "duration",
                                headerName: "Duration",
                                width: 85,
                                editable: false,
                                sortable: false,
                                filterable: false,
                                valueGetter: (value, row) => row.end_time ? dayjs.utc(row.end_time).diff(dayjs.utc(row.start_time), 'minutes') : 0,
                                valueFormatter: (value) => {
                                    return formatDuration(value)
                                }
                            },
                            {
                                field: "recorded_minutes",
                                headerName: "Recorded",
                                width: 85,
                                editable: false,
                                sortable: false,
                                filterable: false,
                                valueGetter: (value, row) => row.service_order_work_history_entries.reduce((a, b) => a + b.recorded_minutes, 0),
                                valueFormatter: (value) => {
                                    return formatDuration(value)
                                }
                            },
                            {
                                field: "user_id",
                                headerName: "Technician",
                                width: 135,
                                editable: false,
                                sortable: true,
                                filterable: false,
                                renderCell: (params) => {
                                    const user = findUser(users, params.row.user_id)
                                    return (
                                        (<Grid container space={1} alignItems="center" sx={{ height: '100%' }}>
                                            <Grid sx={{ pr: 1 }} size="auto">
                                                <span><UserAvatar user={user} tooltip={user?.name} /></span>
                                            </Grid>
                                        </Grid>)
                                    );
                                }
                            },
                            {
                                field: "service_order_id",
                                headerName: "Service Order",
                                width: 175,
                                editable: false,
                                sortable: true,
                                renderCell: (params) => {
                                    return (
                                        (<Button onClick={(e) => { e.preventDefault(); navigate(`/ServiceOrders/${params.value}`) }} href={`/ServiceOrders/${params.value}`}>
                                            SO #{params.value}
                                        </Button>)
                                    );
                                }
                            },
                            {
                                field: "note",
                                headerName: "Details",
                                flex: 1,
                                editable: false,
                                sortable: false,
                                filterable: false,
                                display: 'flex',
                                renderCell: (params) => {
                                    return <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', mt: .5, mb: .5 }}>
                                        {params.row.diagnostic_minutes > 0 && [<span key='dm'>{formatDuration(params.row.diagnostic_minutes)} Diagnostic - {params.row.diagnostic_note}</span>, <br key='dmbr' />]}
                                        {params.row.service_order_work_history_entries.map((entry) =>
                                            [
                                                <span key={entry.id}>{formatDuration(entry.recorded_minutes)} - {entry.service_order_service?.name ?? 'DELETED'} - {entry.note}</span>,
                                                <br key={`${entry.id}-br`} />
                                            ]
                                        )}
                                    </Box>
                                }
                            },
                            {
                                field: 'action',
                                headerName: '',
                                type: 'actions',
                                width: 100,
                                sortable: false,
                                disableClickEventBubbling: true,

                                renderCell: (params) => {
                                    return (
                                        params.row.end_time ?
                                            <Button variant="contained" color="warning" size="small" onClick={() => editWorkHistoryRowClick(params.row)}>Edit</Button>
                                            :
                                            <Button variant="contained" color="error" size="small" onClick={() => editWorkHistoryRowClick(params.row)}>Close</Button>
                                    );
                                },
                            }
                        ]}
                        disableRowSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        pageSizeOptions={[5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'id', sort: 'asc' })
                            }
                        }}
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                    />
                </Grid>
            </Grid>
            <ServiceWorkHistoryModal
                {...workHistoryModal}
                closeModal={closeModal}
            />
        </Box >)
    );
}